import "./header.scss";

import { getKnownLocales, getLocaleFromUrl, getStoredLocale, normalizeLocale, persistLocale, resolvePreferredLocale, } from "@frontend/js/utils/locale";
import SearchIcon from "@frontend/images/icons/SearchIcon";
import CrossIcon from "@frontend/images/icons/CrossIcon";
import { Link, router, usePage } from "@inertiajs/react";
import MenuIcon from "@frontend/images/icons/MenuIcon";
import LogoMobile from "@frontend/images/LogoMobile";
import { useEffect, useState } from "react";
import useT from "@frontend/js/hooks/useT";
import Logo from "@frontend/images/Logo";


const SEARCH_ANIMATION_DURATION_MS = 680;

export default function Header() {
    const { props, url } = usePage();
    const { currentLocale, headerMenu = [], siteLanguages = [] } = props;
    const { t } = useT("site");
    const [isSearchOpen, setIsSearchOpen] = useState(false);
    const [isSearchMounted, setIsSearchMounted] = useState(false);
    const [searchValue, setSearchValue] = useState("");
    const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
    const [isMobileLanguageMenuOpen, setIsMobileLanguageMenuOpen] =
        useState(false);
    const [isMobileLogo, setIsMobileLogo] = useState(
        typeof window !== "undefined" ? window.innerWidth < 950 : false,
    );

    const knownLocales = getKnownLocales(siteLanguages);
    const [pathPart, queryPart] = `${url || ""}`.split("?");
    const pathSegments = pathPart.split("/").filter(Boolean);
    const firstPathSegment = normalizeLocale(pathSegments[0]);
    const activeLocale = resolvePreferredLocale({
        url,
        currentLocale,
        siteLanguages,
    });
    const safeLocale =
        activeLocale ||
        normalizeLocale(currentLocale) ||
        getStoredLocale(knownLocales);

    const extractLocalePrefixFromHref = (hrefValue) => {
        const href = `${hrefValue || ""}`.trim();

        if (!href) {
            return "";
        }

        try {
            const baseOrigin =
                typeof window !== "undefined"
                    ? window.location.origin
                    : "http://localhost";
            const parsedUrl = new URL(href, baseOrigin);

            return normalizeLocale(
                parsedUrl.pathname.split("/").filter(Boolean)[0],
            );
        } catch {
            return "";
        }
    };

    const knownLocalePrefixes = siteLanguages
        .map((lang) => extractLocalePrefixFromHref(lang?.href))
        .filter(Boolean);
    const localePrefixFromPath = normalizeLocale(pathSegments[0]);
    const activeLanguage =
        siteLanguages.find(
            (lang) =>
                normalizeLocale(lang.locale) === normalizeLocale(activeLocale),
        ) ||
        siteLanguages.find(
            (lang) =>
                normalizeLocale(lang.locale) === normalizeLocale(safeLocale),
        ) ||
        null;
    const localePrefixFromActiveLanguage = extractLocalePrefixFromHref(
        activeLanguage?.href,
    );
    const activeLocalePrefix = knownLocalePrefixes.includes(
        localePrefixFromPath,
    )
        ? localePrefixFromPath
        : localePrefixFromActiveLanguage || safeLocale;

    const pathWithoutLocale =
        knownLocales.includes(firstPathSegment) ||
        knownLocalePrefixes.includes(firstPathSegment)
            ? pathSegments.slice(1)
            : pathSegments;

    useEffect(() => {
        const localeFromUrl = getLocaleFromUrl(url, knownLocales);

        if (localeFromUrl) {
            persistLocale(localeFromUrl);
            return;
        }

        if (activeLocale) {
            persistLocale(activeLocale);
        }
    }, [activeLocale, knownLocales, url]);

    useEffect(() => {
        const params = new URLSearchParams(queryPart || "");
        const nextValue = params.get("q") || "";

        setSearchValue(nextValue);
    }, [queryPart]);

    useEffect(() => {
        let closeTimeoutId;

        if (isSearchOpen) {
            setIsSearchMounted(true);
            return undefined;
        }

        if (isSearchMounted) {
            closeTimeoutId = window.setTimeout(() => {
                setIsSearchMounted(false);
            }, SEARCH_ANIMATION_DURATION_MS);
        }

        return () => {
            if (closeTimeoutId) {
                window.clearTimeout(closeTimeoutId);
            }
        };
    }, [isSearchOpen, isSearchMounted]);

    useEffect(() => {
        if (!isSearchMounted) {
            return undefined;
        }

        const lockedScrollY = window.scrollY;
        const blockedKeys = new Set([
            "ArrowUp",
            "ArrowDown",
            "PageUp",
            "PageDown",
            "Home",
            "End",
            " ",
        ]);

        const preventScroll = (event) => {
            if (event.type === "keydown") {
                if (!blockedKeys.has(event.key)) {
                    return;
                }

                const target = event.target;
                const tagName = `${target?.tagName || ""}`.toLowerCase();
                const isEditable =
                    tagName === "input" ||
                    tagName === "textarea" ||
                    Boolean(target?.isContentEditable);

                if (isEditable) {
                    return;
                }
            }

            event.preventDefault();
        };

        const keepScrollPosition = () => {
            if (window.scrollY !== lockedScrollY) {
                window.scrollTo({ top: lockedScrollY, behavior: "auto" });
            }
        };

        window.addEventListener("wheel", preventScroll, { passive: false });
        window.addEventListener("touchmove", preventScroll, {
            passive: false,
        });
        window.addEventListener("keydown", preventScroll, { passive: false });
        window.addEventListener("scroll", keepScrollPosition, {
            passive: true,
        });

        return () => {
            window.removeEventListener("wheel", preventScroll);
            window.removeEventListener("touchmove", preventScroll);
            window.removeEventListener("keydown", preventScroll);
            window.removeEventListener("scroll", keepScrollPosition);
        };
    }, [isSearchMounted]);

    useEffect(() => {
        const handleResize = () => {
            setIsMobileLogo(window.innerWidth < 950);
        };

        window.addEventListener("resize", handleResize);

        return () => {
            window.removeEventListener("resize", handleResize);
        };
    }, []);

    const openSearch = () => {
        setIsSearchMounted(true);
        window.requestAnimationFrame(() => {
            setIsSearchOpen(true);
        });
    };

    const closeSearch = () => {
        setIsSearchOpen(false);
    };

    const isHomePage = pathWithoutLocale.length === 0;
    const logoColor = isHomePage ? "var(--text-alt)" : "var(--primary)";
    const searchColor = isHomePage ? "var(--text-alt)" : "var(--text)";
    const currentPageSlug = pathWithoutLocale[0] || null;
    const normalizedCurrentPageSlug = normalizeLocale(currentPageSlug);
    const isBlogsSection = [
        "blog",
        "blogs",
        "publications",
        "publicatii",
        "publicații",
    ].includes(normalizedCurrentPageSlug);

    const specialMenuRoutes = {
        "case-studies": {
            name: "frontend.page",
            params: { page: "case-studies" },
        },
        methodologies: {
            name: "frontend.page",
            params: { page: "methodologies" },
        },
    };

    const pageSlugAliases = {
        contact: "contacts",
        contacte: "contacts",
    };

    const resolvePageSlug = (value) => {
        const normalized = normalizeLocale(value).replace(/^\/+|\/+$/g, "");

        if (!normalized) {
            return "";
        }

        const slugCandidate = normalized.split("/").pop();

        return pageSlugAliases[slugCandidate] || slugCandidate;
    };

    const resolveSpecialRouteName = (value) => {
        const normalized = normalizeLocale(value).replace(/^\/+|\/+$/g, "");

        if (!normalized) {
            return null;
        }

        const slugCandidate = normalized.split("/").pop();

        return specialMenuRoutes[slugCandidate] || null;
    };

    const isAbsoluteExternalUrl = (value) =>
        /^(https?:)?\/\//i.test(value) || /^(mailto:|tel:|#)/i.test(value);

    const buildSlugHref = (slugValue) => {
        const locale = activeLocalePrefix;
        const normalizedSlug = normalizeLocale(slugValue).replace(
            /^\/+|\/+$/g,
            "",
        );

        if (!normalizedSlug) {
            return locale ? `/${locale}` : "/";
        }

        return locale ? `/${locale}/${normalizedSlug}` : `/${normalizedSlug}`;
    };

    const buildSearchHref = (searchQuery) => {
        const localePrefix = activeLocalePrefix ? `/${activeLocalePrefix}` : "";
        const encodedQuery = encodeURIComponent(searchQuery.trim());

        return `${localePrefix}/search?q=${encodedQuery}`;
    };

    const homeHref = buildSlugHref("");

    const buildLocaleHref = (localeValue) => {
        const normalizedTargetLocale = normalizeLocale(localeValue);
        const knownLanguage = siteLanguages.find(
            (lang) =>
                normalizeLocale(lang.locale) === normalizedTargetLocale &&
                typeof lang.href === "string" &&
                lang.href.trim().length > 0,
        );

        if (knownLanguage) {
            return knownLanguage.href;
        }

        const basePath = pathWithoutLocale.join("/");
        const localePrefix = normalizedTargetLocale
            ? `/${normalizedTargetLocale}`
            : "";
        const localizedPath = basePath
            ? `${localePrefix}/${basePath}`
            : localePrefix || "/";

        return queryPart ? `${localizedPath}?${queryPart}` : localizedPath;
    };

    const getLanguageShortLabel = (localeValue) => {
        const normalizedLocaleValue = normalizeLocale(localeValue);

        if (!normalizedLocaleValue) {
            return "Lang";
        }

        if (normalizedLocaleValue === "ru") {
            return "Ру";
        }

        const shortLabel = normalizedLocaleValue.slice(0, 2);

        return shortLabel.charAt(0).toUpperCase() + shortLabel.slice(1);
    };

    const handleSearchSubmit = (event) => {
        event.preventDefault();

        const nextQuery = searchValue.trim();

        if (!nextQuery) {
            return;
        }

        setIsSearchOpen(false);
        router.visit(buildSearchHref(nextQuery));
    };

    const closeMobileMenu = () => {
        setIsMobileMenuOpen(false);
    };

    const closeMobileLanguageMenu = () => {
        setIsMobileLanguageMenuOpen(false);
    };

    const toggleMobileMenu = () => {
        setIsMobileMenuOpen((prev) => {
            const next = !prev;
            if (next) {
                setIsMobileLanguageMenuOpen(false);
            }

            return next;
        });
    };

    const toggleMobileLanguageMenu = () => {
        setIsMobileMenuOpen(false);
        setIsMobileLanguageMenuOpen((prev) => !prev);
    };

    useEffect(() => {
        const shouldLockScroll = isMobileMenuOpen || isMobileLanguageMenuOpen;

        if (!shouldLockScroll) {
            document.body.style.overflow = "";
            document.body.style.touchAction = "";
            return undefined;
        }

        document.body.style.overflow = "hidden";
        document.body.style.touchAction = "none";

        return () => {
            document.body.style.overflow = "";
            document.body.style.touchAction = "";
        };
    }, [isMobileLanguageMenuOpen, isMobileMenuOpen]);

    const currentLanguageLabel = getLanguageShortLabel(
        activeLocale || currentLocale || safeLocale,
    );
    const normalizedActiveLocale = normalizeLocale(
        activeLocale || currentLocale || "",
    );

    return (
        <header className={isHomePage ? "" : "is-other"}>
            <Link href={homeHref} className="logo">
                {isMobileLogo ? (
                    <LogoMobile color={logoColor} />
                ) : (
                    <Logo color={logoColor} />
                )}
            </Link>

            <button
                type="button"
                className={`headerBurger ${isMobileMenuOpen ? "is-open" : ""}`}
                aria-label={
                    isMobileMenuOpen ? "Close navigation" : "Open navigation"
                }
                aria-expanded={isMobileMenuOpen}
                onClick={toggleMobileMenu}
            >
                <MenuIcon />
            </button>

            <nav>
                {headerMenu.map((item) => {
                    if (!item.url) {
                        return null;
                    }

                    if (item.external) {
                        return (
                            <a
                                key={item.id}
                                href={item.url}
                                target="_blank"
                                rel="noreferrer"
                            >
                                {item.title}
                            </a>
                        );
                    }

                    return (
                        <Link
                            key={item.id}
                            href={item.url}
                            className={item.active ? "is-active" : ""}
                        >
                            {item.title}
                        </Link>
                    );
                })}
            </nav>

            <div
                className={`headerMobileMenu ${isMobileMenuOpen ? "is-open" : ""}`}
                aria-hidden={!isMobileMenuOpen}
            >
                <button
                    type="button"
                    className="headerMobileMenu__overlay"
                    onClick={closeMobileMenu}
                    aria-label="Close mobile navigation"
                />
                <div className="headerMobileMenu__panel">
                    <button
                        className="headerMobileMenu__panel__close"
                        onClick={closeMobileMenu}
                    >
                        <CrossIcon />
                    </button>
                    <nav className="headerMobileMenu__panel__nav">
                        {headerMenu.map((item) => {
                            if (item.external) {
                                const externalUrl = `${item.url || ""}`.trim();
                                const canTreatAsInternalSlug =
                                    externalUrl &&
                                    !isAbsoluteExternalUrl(externalUrl);
                                const normalizedExternalSlug =
                                    canTreatAsInternalSlug
                                        ? resolvePageSlug(externalUrl)
                                        : "";

                                if (normalizedExternalSlug) {
                                    return (
                                        <Link
                                            key={`mobile-${item.id}`}
                                            href={buildSlugHref(
                                                normalizedExternalSlug,
                                            )}
                                            className={
                                                normalizedExternalSlug ===
                                                normalizedCurrentPageSlug
                                                    ? "is-active"
                                                    : ""
                                            }
                                            onClick={closeMobileMenu}
                                        >
                                            {item.title}
                                        </Link>
                                    );
                                }

                                const specialRoute =
                                    resolveSpecialRouteName(externalUrl);

                                if (specialRoute) {
                                    return (
                                        <Link
                                            key={`mobile-${item.id}`}
                                            href={
                                                safeLocale
                                                    ? route(specialRoute.name, {
                                                          ...specialRoute.params,
                                                      })
                                                    : "/"
                                            }
                                            className={
                                                specialRoute.name ===
                                                    "blogs.index" &&
                                                isBlogsSection
                                                    ? "is-active"
                                                    : ""
                                            }
                                            onClick={closeMobileMenu}
                                        >
                                            {item.title}
                                        </Link>
                                    );
                                }

                                return (
                                    <a
                                        key={`mobile-${item.id}`}
                                        href={externalUrl}
                                        target="_blank"
                                        rel="noreferrer"
                                        onClick={closeMobileMenu}
                                    >
                                        {item.title}
                                    </a>
                                );
                            }

                            if (item.slug) {
                                const normalizedSlug = resolvePageSlug(
                                    item.slug,
                                );
                                const specialRoute =
                                    resolveSpecialRouteName(normalizedSlug);
                                const href = specialRoute
                                    ? safeLocale
                                        ? route(specialRoute.name, {
                                              ...specialRoute.params,
                                          })
                                        : "/"
                                    : buildSlugHref(normalizedSlug);
                                const isActive =
                                    normalizedSlug ===
                                    normalizedCurrentPageSlug;

                                return (
                                    <Link
                                        key={`mobile-${item.id}`}
                                        href={href}
                                        className={isActive ? "is-active" : ""}
                                        onClick={closeMobileMenu}
                                    >
                                        {item.title}
                                    </Link>
                                );
                            }

                            if (item.url) {
                                return (
                                    <Link
                                        key={`mobile-${item.id}`}
                                        href={item.url}
                                        className={
                                            item.active ? "is-active" : ""
                                        }
                                        onClick={closeMobileMenu}
                                    >
                                        {item.title}
                                    </Link>
                                );
                            }

                            return null;
                        })}
                    </nav>
                </div>
            </div>

            <div
                className={`headerMobileLanguageMenu ${isMobileLanguageMenuOpen ? "is-open" : ""}`}
                aria-hidden={!isMobileLanguageMenuOpen}
            >
                <button
                    type="button"
                    className="headerMobileLanguageMenu__overlay"
                    onClick={closeMobileLanguageMenu}
                    aria-label="Close language chooser"
                />
                <div className="headerMobileLanguageMenu__panel">
                    <button
                        type="button"
                        className="headerMobileLanguageMenu__panel__close"
                        onClick={closeMobileLanguageMenu}
                        aria-label="Close language chooser"
                    >
                        <CrossIcon />
                    </button>
                    {siteLanguages.map((lang) => (
                        <Link
                            key={`lang-mobile-sheet-${lang.locale}`}
                            href={buildLocaleHref(lang.locale)}
                            onClick={() => {
                                persistLocale(lang.locale);
                                closeMobileLanguageMenu();
                            }}
                            className={
                                normalizeLocale(lang.locale) ===
                                normalizedActiveLocale
                                    ? "is-current"
                                    : ""
                            }
                        >
                            {getLanguageShortLabel(lang.locale)}
                        </Link>
                    ))}
                </div>
            </div>

            <div className="language-switcher">
                <button
                    type="button"
                    className="language-switcher__search"
                    onClick={openSearch}
                    aria-label="Open search"
                >
                    <SearchIcon color={searchColor} />
                </button>
                {siteLanguages.length > 1 && (
                    <>
                        <button
                            type="button"
                            className="language-switcher__current"
                            onClick={toggleMobileLanguageMenu}
                            aria-expanded={isMobileLanguageMenuOpen}
                        >
                            {currentLanguageLabel}
                        </button>
                    </>
                )}
                {siteLanguages.length > 1 && (
                    <div className="language-switcher__languages">
                        {siteLanguages.map((lang) => (
                            <Link
                                key={lang.locale}
                                href={lang.href}
                                onClick={() => persistLocale(lang.locale)}
                                className={
                                    normalizeLocale(lang.locale) ===
                                    activeLocale
                                        ? "is-active"
                                        : ""
                                }
                            >
                                {getLanguageShortLabel(lang.locale)}
                            </Link>
                        ))}
                    </div>
                )}
            </div>
            {isSearchMounted ? (
                <div
                    className={`headerSearch ${isSearchOpen ? "is-open" : "is-closing"}`}
                    role="dialog"
                    aria-modal="true"
                    aria-label="Search"
                >
                    <button
                        type="button"
                        className="headerSearch__overlay"
                        onClick={closeSearch}
                        aria-label="Close search"
                    />

                    <form
                        className="headerSearch__panel"
                        onSubmit={handleSearchSubmit}
                    >
                        <input
                            type="text"
                            value={searchValue}
                            onChange={(event) =>
                                setSearchValue(event.target.value)
                            }
                            placeholder={t("search-placeholder")}
                            autoFocus
                        />
                        <button
                            type="button"
                            className="headerSearch__panel__close"
                            onClick={closeSearch}
                            aria-label="Close search"
                        >
                            <CrossIcon />
                        </button>
                    </form>
                </div>
            ) : null}
        </header>
    );
}
